import React, { useCallback, useEffect, useState } from 'react';

import { Point2D } from '@paragon/geometry-library';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { setSelectedSnap } from '../../store/actions/selectActions';
import { getActiveComponentSnaps, Snap } from '../../store/selectors/getActiveComponentSnaps';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../util/events/types';
import { SnapVisual } from './SnapVisual';

const minimumDistanceFromMouseToSnap = 5;

const snapTypeToSize: { Midline: number; Intersection: number; Vertex: number; Centroid: number } = {
  Intersection: 0.4,
  Vertex: 0.5,
  Midline: 0.5,
  Centroid: 0.7,
};

export function SnapOverlay() {
  const dispatch = useAppDispatch();
  const snaps = useAppSelector(getActiveComponentSnaps);

  const [closestSnapInRange, setClosestSnapInRange] = useState<Snap | null>(null);

  const locateClosestSnapInRange = useCallback(
    (testPoint: Point2D) => {
      let closestDistance = Infinity;
      let closestSnap: Snap | null = null;

      for (const snap of snaps) {
        const distance = testPoint.distanceTo(snap.position);
        if (distance < closestDistance && distance < minimumDistanceFromMouseToSnap) {
          closestDistance = distance;
          closestSnap = snap;
        }
      }
      return closestSnap;
    },
    [snaps],
  );

  const proximitySelect = useCallback(
    (event: GlobalThreeJSDomObjectEvent) => {
      const mousePoint = Point2D.fromObject(event.eventPosition.worldPosition);
      const closestSnapInRange = locateClosestSnapInRange(mousePoint);
      if (!closestSnapInRange) return;

      dispatch(setSelectedSnap({ x: closestSnapInRange.position.x, y: closestSnapInRange.position.y }));
    },
    [dispatch, locateClosestSnapInRange],
  );

  useEffect(() => {
    function showClosestSnap(event: GlobalThreeJSDomObjectEvent) {
      const mousePoint = Point2D.fromObject(event.eventPosition.worldPosition);
      const closestSnapInRange = locateClosestSnapInRange(mousePoint);
      setClosestSnapInRange(closestSnapInRange);
    }

    threeJsDom.addGlobalListener('mousemove', showClosestSnap);
    threeJsDom.addGlobalListener('click', proximitySelect);

    return () => {
      threeJsDom.removeGlobalListener('mousemove', showClosestSnap);
      threeJsDom.removeGlobalListener('click', proximitySelect);
    };
  }, [locateClosestSnapInRange, proximitySelect]);

  if (snaps.length === 0) return null;

  return (
    closestSnapInRange && (
      <SnapVisual
        snapType={closestSnapInRange.snapType}
        size={snapTypeToSize[closestSnapInRange.snapType]}
        position={closestSnapInRange.position.to3D().toArray()}
      />
    )
  );
}
